import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import {
    AllCoachesSalaryRow,
    AllCoachesSalaryTotals,
    failureReasonText,
    isRetiredCoach,
    rowBadges,
    SalaryRowBadge,
} from './allCoachesSalary';

export const SALARY_BADGE_LABEL: Record<SalaryRowBadge, string> = {
    retired: '퇴사',
    saved: '저장됨',
    unsaved: '저장 전',
    bracketMissing: '구간 미설정',
    integrity: '일부 수업 제외됨',
    failed: '불러오지 못함',
};

export const ALL_COACHES_SALARY_HEADERS = [
    '강사',
    '매출',
    '수업료',
    '인센티브(원)',
    '기본급',
    '기타 수당(원)',
    '3.3% 공제',
    '최종 급여',
    '상태',
];

/** 엑셀에 들어갈 행(표와 같은 값). 인센티브·기타 수당은 비율이 아니라 원 금액이다. 공제는 실제 차감액(비과세 0). */
export const buildAllCoachesSalarySheetRows = (
    rows: AllCoachesSalaryRow[],
    totals: AllCoachesSalaryTotals,
): (string | number)[][] => {
    const body = rows.map((row) => {
        if (row.state !== 'ok') {
            const failedText = row.state === 'failed' ? `불러오지 못함(${failureReasonText(row.reason)})` : '불러오는 중';
            const status = (isRetiredCoach(row.coach) ? '퇴사, ' : '') + failedText;
            return [row.coach.value, '', '', '', '', '', '', '', status];
        }
        return [
            row.coach.value,
            row.calc.settlement,
            row.calc.classFee,
            row.calc.incentiveKRW,
            row.calc.baseSalary,
            row.calc.etcBonusKRW,
            row.calc.deductionApplied === 0 ? 0 : -row.calc.deductionApplied,
            row.calc.finalSalary,
            rowBadges(row)
                .map((badge) => SALARY_BADGE_LABEL[badge])
                .join(', '),
        ];
    });
    const notes: string[] = [];
    if (totals.failedCount > 0) notes.push(`불러오지 못함 ${totals.failedCount}명 제외`);
    if (totals.integrityCount > 0) notes.push(`일부 수업 제외 ${totals.integrityCount}명 포함`);
    const totalRow = [
        `합계 (${totals.includedCount}명)`,
        totals.settlement,
        totals.classFee,
        totals.incentiveKRW,
        totals.baseSalary,
        totals.etcBonusKRW,
        totals.deductionApplied === 0 ? 0 : -totals.deductionApplied,
        totals.finalSalary,
        notes.join(' · '),
    ];
    return [ALL_COACHES_SALARY_HEADERS, ...body, totalRow];
};

export const exportAllCoachesSalaryExcel = ({
    rows,
    totals,
    monthLabel,
    includeGroupClassInSettlement,
    vatNote,
}: {
    rows: AllCoachesSalaryRow[];
    totals: AllCoachesSalaryTotals;
    monthLabel: string;
    includeGroupClassInSettlement: boolean;
    vatNote?: string;
}) => {
    const sheetRows = buildAllCoachesSalarySheetRows(rows, totals);
    const sheet = XLSX.utils.aoa_to_sheet([
        [`${monthLabel} 전체 강사 급여`],
        [
            `저장된 입력값(인센티브·기본급·수당·공제)과 지금 수업·매출로 계산한 값 · 월 전체 수업(예정 포함) 기준 · 그룹수업 합산 ${
                includeGroupClassInSettlement ? 'ON' : 'OFF'
            }${vatNote ? ` · ${vatNote}` : ''}`,
        ],
        [],
        ...sheetRows,
    ]);
    const numberColumns = [1, 2, 3, 4, 5, 6, 7];
    for (let r = 3; r < 3 + sheetRows.length; r += 1) {
        numberColumns.forEach((c) => {
            const ref = XLSX.utils.encode_cell({ r, c });
            if (sheet[ref] && sheet[ref].t === 'n') sheet[ref].z = '#,##0';
        });
    }
    sheet['!cols'] = [{ wch: 14 }, ...numberColumns.map(() => ({ wch: 16 })), { wch: 30 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, '전체 강사');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${monthLabel} 전체 강사 급여.xlsx`);
};
